import { Site } from '@core/generated/gql-types';
import { generateFriendlyAddress } from '@core/util/google-places.service';

const NO_SITES_LABEL = 'No sites available';

export interface SiteMultiSelectProps {
  sites: Site[];
  selectedSiteIds: string[];
  onChange: (siteIds: string[]) => void;
  disabled?: boolean;
}

export const SiteMultiSelect = ({
  sites,
  selectedSiteIds,
  onChange,
  disabled,
}: SiteMultiSelectProps) => {
  if (sites.length === 0) {
    return <p className="text-sm text-gray-500">{NO_SITES_LABEL}</p>;
  }

  const toggleSite = (siteId: string) => {
    if (selectedSiteIds.includes(siteId)) {
      onChange(selectedSiteIds.filter((id) => id !== siteId));
      return;
    }
    onChange([...selectedSiteIds, siteId]);
  };

  const selectAll = () => onChange(sites.map((site) => site.id));
  const clearAll = () => onChange([]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">
          {selectedSiteIds.length} of {sites.length} selected
        </span>
        <div className="flex gap-3">
          <button
            type="button"
            disabled={disabled}
            onClick={selectAll}
            className="text-xs font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-50"
          >
            Select all
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={clearAll}
            className="text-xs font-medium text-gray-600 hover:text-gray-700 disabled:opacity-50"
          >
            Clear
          </button>
        </div>
      </div>
      <div className="max-h-48 space-y-2 overflow-y-auto rounded-md border border-gray-200 p-3">
        {sites.map((site) => {
          const addressLabel = generateFriendlyAddress(site.address ?? {});
          return (
            <label
              key={site.id}
              className="flex cursor-pointer items-start gap-2 text-sm"
            >
              <input
                type="checkbox"
                disabled={disabled}
                checked={selectedSiteIds.includes(site.id)}
                onChange={() => toggleSite(site.id)}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium text-gray-900">{site.name}</span>
                {addressLabel ? (
                  <span className="block text-xs text-gray-500">
                    {addressLabel}
                  </span>
                ) : null}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};
